import React, { useEffect, useState } from "react";

import { Input } from "../../components/form/formControls";

import { fetchTeamBalanceList } from "../../api/product-service";
import { CUSTOMER_API_URL } from "../../api/urlMetaData";

import { useSelector } from "react-redux";

import { APP_ROLES } from "../../consts/AppRoles";

import Drawer from "../../components/ui/Drawer";
import Table, { Data, Row } from "../../components/ui/table/table";
import { ButtonGroup, Button } from "../../components/ui/buttons/Button";

import {
  formatAmount,
  formatCSVValue,
  getAmountWithCurrency,
} from "../../utils/utils";
import Section from "../../layouts/Section";

import Skeleton from "react-loading-skeleton";
import CustomerService from "../../api/customer-service";
import {
  fetchAvailableBalance,
  updateTeamBalance,
} from "../../api/wallet-service";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "../../hooks/useDebounce";
import { toast } from "react-toastify";
import { useLocale } from "../../hooks/useLocale";

const TeamBalanceDrawer = ({ title, onClose }) => {
  const currentUser = useSelector((s) => s.auth.currentUser);
  const currentProgram = useSelector((s) => s.global.currentProgram);

  const [teamBalanceList, setTeamBalanceList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  const { localize } = useLocale(["cards"]);

  const balanceQuery = useQuery(["cards/balance"], () =>
    fetchAvailableBalance({
      userHashId: currentUser.userHashId,
      programHashId: currentProgram,
      currency: "AED",
    })
  );

  useEffect(() => {
    setListLoading(true);
    fetchTeamBalanceList({
      userHashId: currentUser.userHashId,
      programCode: currentProgram,
      currencyCode: "AED",
    }).then((res) => {
      setTeamBalanceList((prev) => [...res]);
      setListLoading(false);
    });
  }, []);

  const handleAmount = (e, payload) => {
    const { value } = e.target;
    const { index, list } = payload;
    const item = list[index];

    if (payload.type === "Credit") {
      document.getElementById(`input-debit-${index}`).value = "";
      document.getElementById(`input-credit-${index}`).value = formatAmount(
        formatCSVValue(value)
      );
    } else {
      document.getElementById(`input-credit-${index}`).value = "";
      document.getElementById(`input-debit-${index}`).value = formatAmount(
        formatCSVValue(value)
      );
    }

    const internalReferanceNo = document.getElementById(
      `input-int-ref-${index}`
    ).value;

    const updatedItem = {
      ...item,
      transactionType: value ? payload.type : null,
      amount: formatCSVValue(value) || 0,
      internalReferanceNo,
      subOrdinateHashId: item.userHashId,
    };

    list[index] = updatedItem;
    setTeamBalanceList(list);
  };
  const debouncedHandleAmount = useDebounce(handleAmount);

  const handleUpdateBalance = (e) => {
    e.preventDefault();

    let prefundBatchObjs = teamBalanceList.filter((i) => i.amount);

    prefundBatchObjs = prefundBatchObjs.map((i) => {
      return {
        transactionTypes: i.transactionType,
        amount: i.amount,
        internalReferanceNo: i.internalReferanceNo,
        subOrdinateHashId: i.userHashId,
      };
    });

    const payload = {
      programCode: currentProgram,
      superVisorHashId: currentUser.userHashId,
      productType: currentUser.productType,
      clientHashId: currentUser.clientHashId,
      prefundBatchObjs,
    };

    setLoading(true);
    updateTeamBalance(payload)
      .then((res) => {
        setLoading(false);
        toast(res.message, {
          type: "success",
        });
        onClose();
      })
      .catch((err) => {
        setLoading(false);
        onClose();
      });
  };

  return (
    <>
      <Drawer title={title} onClose={onClose} width={900} noClose>
        <Section>
          <div className="row gy-4">
            <div className="col-sm-12 col-md-6 col-lg-3">
              <TransporterLogo
                clientLogoFileName={currentUser?.clientLogoFileName}
              />
            </div>

            <div className="col-sm-12 col-md-6 col-lg-4">
              <h6 className="text-uppercase">{localize("teamBalance.transporter")}</h6>
              <span className="text-uppercase">{currentUser?.clientName}</span>
            </div>
            <div className="col-sm-12 col-md-6 col-lg-4">
              <h6 className="text-uppercase">{localize("teamBalance.availablePrefund")}</h6>
              <span className="text-uppercase">
                {balanceQuery.isLoading ? (
                  <Skeleton width={120} />
                ) : (
                  getAmountWithCurrency(
                    formatAmount(balanceQuery.data?.balance),
                    "AED"
                  )
                )}
              </span>
            </div>
          </div>
        </Section>
        <>
          <form onSubmit={handleUpdateBalance}>
            <fieldset disabled={loading}>
              <Table
                loading={listLoading}
                cols={localize("teamBalance.table.cols", { returnObjects: true })}
                dataSize={teamBalanceList?.length}
              >
                <>
                  {teamBalanceList?.map((item, index) => {
                    return (
                      <>
                        <Row key={`team-balance-item-${index}`}>
                          <Data>{item.name ? item.name : "-"}</Data>
                          <Data>{APP_ROLES[item.productType]}</Data>
                          <Data>
                            {item.avlBalance
                              ? getAmountWithCurrency(
                                  formatAmount(item.avlBalance),
                                  "AED"
                                )
                              : "-"}
                          </Data>
                          <Data>
                            {item.holdBalace
                              ? getAmountWithCurrency(
                                  formatAmount(item.holdBalace),
                                  "AED"
                                )
                              : "-"}
                          </Data>
                          <Data>
                            {item.walletLoadLimit
                              ? getAmountWithCurrency(
                                  formatAmount(item.walletLoadLimit),
                                  "AED"
                                )
                              : "-"}
                          </Data>
                          <Data>
                            {item.paymentLimit
                              ? getAmountWithCurrency(
                                  formatAmount(item.paymentLimit),
                                  "AED"
                                )
                              : "-"}
                          </Data>
                          <Data>
                            <Input
                              id={`input-credit-${index}`}
                              data-index={index}
                              onChange={(e) => {
                                debouncedHandleAmount(e, {
                                  type: "Credit",
                                  id: item.userHashId,
                                  index,
                                  list: teamBalanceList,
                                });
                              }}
                            />
                          </Data>
                          <Data>
                            <Input
                              id={`input-debit-${index}`}
                              data-index={index}
                              onChange={(e) => {
                                debouncedHandleAmount(e, {
                                  type: "Debit",
                                  id: item.userHashId,
                                  index,
                                  list: teamBalanceList,
                                });
                              }}
                            />
                          </Data>
                          <Data>
                            <Input id={`input-int-ref-${index}`} />
                          </Data>
                        </Row>
                      </>
                    );
                  })}
                </>
                {/* show empty list */}
              </Table>
            </fieldset>
            <ButtonGroup>
              <Button
                loading={loading}
                className="btn btn-primary"
                text={localize("teamBalance.table.actions.updateTeamBalance")}
              />
              <Button
                className="btn btn-light"
                text={localize("teamBalance.table.actions.cancel")}
                onClick={onClose}
              />
            </ButtonGroup>
          </form>
        </>
      </Drawer>
    </>
  );
};

export const TransporterLogo = (clientLogoFileName = "") => {
  const [src, setSrc] = useState("");
  const [loading, setLoading] = useState(true);

  const handleImageDownload = async () => {
    const res = await CustomerService.post(CUSTOMER_API_URL.DOWNLOAD_IMAGE, {
      fileName: clientLogoFileName.clientLogoFileName,
    });

    if (res) {
      setSrc("data:image/jpeg;base64," + res?.data?.body);
      setLoading(false);
    }
  };

  useEffect(() => {
    handleImageDownload().then();
  }, []);

  if (loading) return <Skeleton width={50} circle height={50} />;

  return (
    <>
      <div className="text-center">
        <img
          className="img-fluid text-center"
          style={{ height: "50px" }}
          src={src}
          alt="transporter logo"
        />
      </div>
    </>
  );
};

export default TeamBalanceDrawer;
